/**
 * What winning takes: cities, captives, and civilisations knocked out of the game.
 *
 * Separate from the fight itself because none of it is about dice. A city changes
 * hands, loses a citizen and some of what was built in it; units that cannot defend
 * themselves are taken rather than killed; a civilisation with no cities and nothing
 * to found one with is gone. Each is a rule of the game that the command pipeline,
 * the AI and the tests all need to see happen the same way.
 */

import type { Content } from '../../../content/schema/index.js';
import type { GameEvent } from '../commands/types.js';
import { assignWorkedTiles, citiesOf, settleBorders } from '../cities/index.js';
import { addBuilding } from '../cities/production.js';
import { recordLoss } from '../diplomacy/index.js';
import { remember } from '../diplomacy/memory.js';
import { chance, type RngState } from '../rng/index.js';
import { tileCount } from '../state/grid.js';
import { NONE, type City, type GameState, type Id, type Unit } from '../state/types.js';
import { allUnits, unitDef } from '../units/index.js';
import { recomputeVision } from '../vision/index.js';
import { wrappedDistance } from '../state/start.js';

/**
 * Take units that cannot defend themselves. They change sides where they stand,
 * orders cleared and movement spent — a captured settler is a settler for its new
 * owner, which is the one consolation of having left it unguarded.
 */
export function captureUnits(state: GameState, content: Content, captives: readonly Unit[], by: Id): GameEvent[] {
  const events: GameEvent[] = [];
  for (const unit of captives) {
    const from = unit.owner;
    recordLoss(state, from, by, unitDef(content, unit).cost);
    // Aircraft are wrecked on the ground, not flown away by the new owner (ADR-0065).
    if (unitDef(content, unit).domain === 'air') {
      const at = state.map.width * unit.row + unit.col;
      delete state.units[unit.id];
      events.push({ t: 'AircraftLost', unitId: unit.id, unitType: unit.type, owner: from, at, cause: 'cityFell' });
      continue;
    }
    unit.owner = by;
    unit.orders = null;
    unit.movesLeft = 0;
    unit.aboard = NONE;
    // Anyone aboard a captured ship in harbour is ashore now, and still its old owner's.
    for (const passenger of allUnits(state)) if (passenger.aboard === unit.id) passenger.aboard = NONE;
    events.push({ t: 'UnitCaptured', unitId: unit.id, unitType: unit.type, from, to: by });
  }
  return events;
}

/**
 * A city changes hands.
 *
 * It loses `captureCitizensLost` citizens — never its last, because Mark chose to keep
 * captured cities rather than raze them — and each building survives a roll against
 * `buildingLossChance`. The palace never survives: it is the old owner's seat, and it
 * moves to their largest remaining city so their empire still has a centre.
 *
 * The ground goes with the city: it keeps its culture and its border, and ownership is
 * settled afresh from the cities as they now stand (ADR-0057).
 */
export function captureCity(state: GameState, content: Content, city: City, by: Id, rng: RngState): GameEvent[] {
  const rules = content.rules.combat;
  const from = city.owner;
  const map = state.map;

  const lostCitizens = Math.min(rules.captureCitizensLost, city.population - 1);
  city.population -= lostCitizens;

  const lost: string[] = [];
  let hadPalace = false;
  const kept: number[] = [];
  for (const id of city.buildings) {
    const def = content.buildings.get(id);
    if (def.effects.isSeatOfGovernment) {
      hadPalace = true;
      lost.push(def.name);
    } else if (def.wonder) {
      // A wonder is too great to burn: it passes to the conqueror (ADR-0057).
      kept.push(id);
    } else if (chance(rng, rules.buildingLossChance)) {
      lost.push(def.name);
    } else {
      kept.push(id);
    }
  }
  city.buildings = kept;

  // Its citizens stop working whatever they were working; the new owner's governor
  // assigns them afresh once the borders are settled.
  for (const tile of city.workedTiles) if (map.workedBy[tile] === city.id) map.workedBy[tile] = NONE;
  city.workedTiles = [];
  city.owner = by;
  city.queue = [];
  city.productionStore = 0;
  city.foodStore = Math.floor(city.foodStore / 2);
  city.governed = true;
  city.specialist = NONE;

  const remaining = citiesOf(state, from);
  // The city keeps its culture, and so its border: ownership follows from the cities
  // as they now stand (ADR-0057).
  settleBorders(state, content);
  assignWorkedTiles(state, content, city);

  if (hadPalace && remaining.length > 0) {
    const seat = content.buildings.all.find((b) => b.effects.isSeatOfGovernment === true);
    const largest = [...remaining].sort((x, y) => y.population - x.population || x.id - y.id)[0]!;
    if (seat) addBuilding(largest, seat.id);
  }
  for (const other of remaining) assignWorkedTiles(state, content, other);

  recordLoss(state, from, by, content.rules.diplomacy.cityLossValue);
  remember(state, from, by, 'cityTaken');

  const events: GameEvent[] = [
    { t: 'CityCaptured', cityId: city.id, name: city.name, from, to: by, lost, citizensLost: lostCitizens },
  ];
  recomputeVision(state, content, from);
  recomputeVision(state, content, by);
  events.push(...checkDefeat(state, content, from));
  return events;
}

/**
 * Hand a city over in peace (ADR-0071): unlike a capture, nothing is burned and
 * nobody dies. Its citizens work for the new owner from now on; the old owner's
 * soldiers inside walk home to the nearest of its other cities. Never the palace or a
 * last city — the deal that gives it away refuses those.
 */
export function cedeCity(state: GameState, content: Content, city: City, to: Id): GameEvent[] {
  const from = city.owner;
  const map = state.map;
  const at = map.width * city.row + city.col;
  const home = citiesOf(state, from)
    .filter((c) => c.id !== city.id)
    .sort((x, y) => wrappedDistance(map, at, map.width * x.row + x.col) - wrappedDistance(map, at, map.width * y.row + y.col) || x.id - y.id)[0];
  for (const unit of allUnits(state)) {
    if (unit.owner !== from || unit.row * map.width + unit.col !== at || !home) continue;
    unit.col = home.col;
    unit.row = home.row;
    unit.orders = null;
  }
  for (const tile of city.workedTiles) if (map.workedBy[tile] === city.id) map.workedBy[tile] = NONE;
  city.workedTiles = [];
  city.owner = to;
  city.queue = [];
  city.productionStore = 0;
  city.governed = true;
  city.specialist = NONE;
  settleBorders(state, content);
  assignWorkedTiles(state, content, city);
  for (const other of citiesOf(state, from)) assignWorkedTiles(state, content, other);
  remember(state, from, to, 'cityTaken');
  recomputeVision(state, content, from);
  recomputeVision(state, content, to);
  return [{ t: 'CityCeded', cityId: city.id, name: city.name, from, to }];
}

/**
 * Knock a civilisation out if it has no cities and nothing left to found one with.
 *
 * Whatever units it still has disband — an army with no country does not keep
 * fighting in this game — its territory is released, and its wars end, because there
 * is nobody left to make peace with.
 */
export function checkDefeat(state: GameState, content: Content, civId: Id): GameEvent[] {
  const civ = state.civs[civId];
  if (!civ || civ.defeated) return [];
  if (citiesOf(state, civId).length > 0) return [];
  const units = allUnits(state).filter((u) => u.owner === civId);
  if (units.some((u) => unitDef(content, u).abilities.includes('found_city'))) return [];

  civ.defeated = true;
  for (const unit of units) delete state.units[unit.id];
  for (let i = 0; i < tileCount(state.map); i++) if (state.map.owner[i] === civId) state.map.owner[i] = NONE;
  for (const relation of state.relations) {
    if (relation.a !== civId && relation.b !== civId) continue;
    relation.war = false;
    relation.peaceOfferedBy = NONE;
    relation.peaceGive = [];
    relation.peaceGet = [];
    relation.campaignA = null;
    relation.campaignB = null;
  }
  recomputeVision(state, content, civId);
  return [{ t: 'CivDefeated', civId, name: civ.name }];
}
